import { getLogger } from '../socket-worker/utils';
import { SocketConnection } from './socket-connection';
import * as EventEmitter from '@/utils/event-emitter';
import { GetSocketOptions } from '../socket-worker/types';

const log = getLogger('bl-socket:manager');
const silly = getLogger('bl-socket-silly:manager');

const improperUsageWarning = `Error: using the same socket namespace manually and from a hook is deprecated and can lead to accidental socket disconnects.`;

class SocketManager {
  private sockets: Map<string, SocketConnection> = new Map();
  private chatSockets: Map<string, SocketConnection> = new Map();
  private transcriptSockets: Map<string, SocketConnection> = new Map();

  private socketsFromHook: Set<string> = new Set();
  private chatSocketsFromHook: Set<string> = new Set();
  private transcriptSocketsFromHook: Set<string> = new Set();

  private toLeave: Set<string> = new Set();
  private toHave: Set<string> = new Set();

  private chatToHave: Set<string> = new Set();
  private transcriptsToHave: Set<string> = new Set();

  private forcedDisconnect = false;
  private changed = false;

  constructor() {
    setInterval(() => this.tick(), 1000);
    EventEmitter.on('socket-disconnect', this.disconnectAll);
    EventEmitter.on('socket-reconnect', this.reconnectAll);
  }

  private disconnectAll = (opts?: { forceDisconnect: boolean }) => {
    for (const socket of this.sockets.values()) {
      socket.end();
    }

    for (const socket of this.chatSockets.values()) {
      socket.end();
    }

    for (const socket of this.transcriptSockets.values()) {
      socket.end();
    }

    this.forcedDisconnect = opts?.forceDisconnect || false;
    EventEmitter.off('socket-disconnect');
  };

  private reconnectAll = () => {
    for (const socket of this.sockets.values()) {
      socket.setupConnection();
    }

    for (const socket of this.chatSockets.values()) {
      socket.setupConnection();
    }

    for (const socket of this.transcriptSockets.values()) {
      socket.setupConnection({ newWorkerForTranscripts: this.forcedDisconnect });
    }

    this.forcedDisconnect = false;
    EventEmitter.on('socket-disconnect', this.disconnectAll);
  };

  private getSocketsFromHook = (options?: GetSocketOptions) => {
    if (options?.chat) {
      return this.chatSocketsFromHook;
    } else if (options?.transcripts) {
      return this.transcriptSocketsFromHook;
    }
    return this.socketsFromHook;
  };

  private getSockets = (options?: GetSocketOptions) => {
    if (options?.chat) {
      return this.chatSockets;
    } else if (options?.transcripts) {
      return this.transcriptSockets;
    }
    return this.sockets;
  };

  private getToHave = (options?: GetSocketOptions) => {
    if (options?.chat) {
      return this.chatToHave;
    } else if (options?.transcripts) {
      return this.transcriptsToHave;
    }

    return this.toHave;
  };

  public get = (ns: string, options?: GetSocketOptions): SocketConnection => {
    const socketsFromHook = this.getSocketsFromHook(options);

    if (socketsFromHook.has(ns)) {
      if (import.meta.env.VITE_STAGE === 'local') {
        throw new Error(`${improperUsageWarning} ${ns} ${JSON.stringify(options)}`);
      }

      console.warn(improperUsageWarning, ns, options);
    }

    silly(`Mounted ${ns}`);
    const sockets = this.getSockets(options);
    const toHave = this.getToHave(options);
    toHave.add(ns);
    if (sockets.has(ns)) {
      silly(`Reusing ${ns}`);
      return sockets.get(ns) as SocketConnection;
    } else {
      silly(`Creating ${ns}`);
      const socket = new SocketConnection(ns, options);
      sockets.set(ns, socket);
      this.changed = true;
      return socket;
    }
  };

  public getFromHook = (ns: string, options?: GetSocketOptions): SocketConnection => {
    const socketsFromHook = this.getSocketsFromHook(options);
    const toHave = this.getToHave(options);
    const sockets = this.getSockets(options);

    silly(`Mounted ${ns}`);
    toHave.add(ns);
    socketsFromHook.add(ns);
    if (sockets.has(ns)) {
      silly(`Reusing ${ns}`);
      return sockets.get(ns) as SocketConnection;
    } else {
      silly(`Creating ${ns}`);
      const socket = new SocketConnection(ns, options);
      sockets.set(ns, socket);
      this.changed = true;
      return socket;
    }
  };

  public leave = (ns: string) => {
    this.toLeave.add(ns);
    silly(`Dismounted ${ns}`);
  };

  private iterateLeave = (ns: string, options?: { chat?: boolean; transcripts?: boolean }) => {
    const sockets = this.getSockets(options);
    const toHave = this.getToHave(options);

    if (!toHave.has(ns)) {
      this.changed = true;
      silly(`Leaving ${ns}`);
      const socket = sockets.get(ns);
      const listeners = socket?.getListeners();
      if (socket && (!listeners || listeners?.length === 0)) {
        sockets.get(ns)?.disconnect();
        sockets.delete(ns);
        silly(`Left ${ns}`);
      } else if (socket) {
        silly(`Cannot leave ${ns}, ${listeners} remain.`);
      }
    }
  };

  private tick = () => {
    for (const ns of this.toLeave) {
      this.iterateLeave(ns);
      this.iterateLeave(ns, { chat: true });
      this.iterateLeave(ns, { transcripts: true });
    }

    if (this.changed) {
      log(`Should have channels`, Array.from(this.sockets.keys()));
    }

    this.toHave = new Set();
    this.chatToHave = new Set();
    this.transcriptsToHave = new Set();

    this.toLeave = new Set();
    this.changed = false;
  };
}

export default new SocketManager();
